interface NotificationCenterItem {
  id: string;
  type: string;
  unread: boolean;
  createdAt: string | null;
  expiresAt: string | null;
  readAt: string | null;
  target?: { path?: string };
  publicPayload?: { title?: string; body?: string };
}

interface NotificationCenterPayload {
  ok?: boolean;
  notifications?: unknown;
  unreadCount?: unknown;
  nextCursor?: unknown;
  error?: unknown;
}

interface NotificationCenterElements {
  section: HTMLElement | null;
  list: HTMLElement | null;
  status: HTMLElement | null;
  unreadBadge: HTMLElement | null;
  refreshButton: HTMLButtonElement | null;
  readAllButton: HTMLButtonElement | null;
  moreButton: HTMLButtonElement | null;
}

interface NotificationCenterState {
  notifications: NotificationCenterItem[];
  unreadCount: number;
  // 개인 안 읽은 메시지 총계(= 사이드바 방별 안 읽음의 합, /api/rooms/unread-count·WS
  // message-unread-total). 앱 배지(navigator.setAppBadge)는 이 값 + 알림 unreadCount 의 합이다.
  messageUnreadTotal: number;
  // 앱 배지 합산의 두 소스가 각각 한 번이라도 서버 값으로 확정됐는지. 한쪽이 아직 모르는 값(초기
  // 0)인 채로 합을 쓰면 서비스워커가 맞춰 둔 배지를 잘못 낮추거나 지우므로, 둘 다 확정되기
  // 전에는 배지를 건드리지 않는다(서비스워커 syncNotificationAppBadge 와 같은 규칙).
  unreadCountKnown: boolean;
  messageUnreadTotalKnown: boolean;
  nextCursor: string | null;
  loading: boolean;
  error: string | null;
  loadingMore: boolean;
  activeReadId: string | null;
  focusNotificationId: string | null;
  retryNotificationId: string | null;
  // "모두 읽음" 요청이 진행 중인지 — 중복 클릭 방지·버튼 비활성.
  markingAllRead: boolean;
}

const NOTIFICATION_CENTER_SYNC_KEY = "bigtionax-notification-center-sync";
const NOTIFICATION_CENTER_PAGE_SIZE = 10;
const notificationCenterState: NotificationCenterState = {
  notifications: [],
  unreadCount: 0,
  messageUnreadTotal: 0,
  unreadCountKnown: false,
  messageUnreadTotalKnown: false,
  nextCursor: null,
  loading: false,
  error: null,
  loadingMore: false,
  activeReadId: null,
  focusNotificationId: null,
  retryNotificationId: null,
  markingAllRead: false,
};

function notificationCenterRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function notificationCenterString(value: unknown): value is string {
  return typeof value === "string";
}

function notificationCenterElements(): NotificationCenterElements {
  return {
    section: document.getElementById("notificationCenterSection"),
    list: document.getElementById("notificationCenterList"),
    status: document.getElementById("notificationCenterStatus"),
    unreadBadge: document.getElementById("notificationCenterUnreadBadge"),
    refreshButton: document.getElementById("notificationCenterRefreshBtn") as HTMLButtonElement | null,
    readAllButton: document.getElementById("notificationCenterReadAllBtn") as HTMLButtonElement | null,
    moreButton: document.getElementById("notificationCenterMoreBtn") as HTMLButtonElement | null,
  };
}

function normalizeNotificationCenterItem(value: unknown): NotificationCenterItem | null {
  if (!notificationCenterRecord(value) || !notificationCenterString(value.id) || !notificationCenterString(value.type)) return null;
  return {
    id: value.id,
    type: value.type,
    unread: value.unread === true,
    createdAt: notificationCenterString(value.createdAt) ? value.createdAt : null,
    expiresAt: notificationCenterString(value.expiresAt) ? value.expiresAt : null,
    readAt: notificationCenterString(value.readAt) ? value.readAt : null,
    target: notificationCenterRecord(value.target) ? value.target : undefined,
    publicPayload: notificationCenterRecord(value.publicPayload) ? value.publicPayload : undefined,
  };
}

function notificationCenterEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// 칩 종류는 실제 emitNotification 생산 type 기준이다(ws-handler·work·routine·factory·company runtime).
// 정확 일치를 먼저 보고, project_sheet_<event> 처럼 이벤트가 붙는 type은 접두사로 묶는다.
type NotificationCenterKind = "chat" | "work" | "routine" | "factory" | "company" | "system" | "other";

const NOTIFICATION_CENTER_KIND_LABELS: Record<NotificationCenterKind, string> = {
  chat: "채팅",
  work: "업무",
  routine: "자동화",
  factory: "팩토리",
  company: "회사",
  system: "테스트",
  other: "알림",
};

const NOTIFICATION_CENTER_TYPE_KINDS: Record<string, NotificationCenterKind> = {
  room_message: "chat",
  room_mention: "chat",
  room_reply: "chat",
  assistant_completion: "chat",
  agent_completion: "chat",
  approval_needed: "work",
  project_deleted: "company",
  team_member_record_changed: "company",
  pwa_test_notification: "system",
};

const NOTIFICATION_CENTER_KIND_PREFIXES: Array<[string, NotificationCenterKind]> = [
  ["chat_", "chat"],
  ["work_", "work"],
  ["routine_", "routine"],
  ["factory_", "factory"],
  ["company_", "company"],
  ["project_sheet_", "company"],
];

function notificationCenterTypeKind(type: string): NotificationCenterKind {
  const exact = NOTIFICATION_CENTER_TYPE_KINDS[type];
  if (exact) return exact;
  const prefixed = NOTIFICATION_CENTER_KIND_PREFIXES.find(([prefix]) => type.startsWith(prefix));
  return prefixed ? prefixed[1] : "other";
}

function notificationCenterTypeLabel(type: string): string {
  return NOTIFICATION_CENTER_KIND_LABELS[notificationCenterTypeKind(type)];
}

function notificationCenterTitle(item: NotificationCenterItem): string {
  const raw = notificationCenterString(item.publicPayload?.title) ? item.publicPayload?.title : "새 알림";
  return raw.length > 56 ? `${raw.slice(0, 56)}…` : raw;
}

function notificationCenterBody(item: NotificationCenterItem): string {
  const raw = notificationCenterString(item.publicPayload?.body) ? item.publicPayload?.body : "";
  return raw.length > 110 ? `${raw.slice(0, 110)}…` : raw;
}

function notificationCenterPath(item: NotificationCenterItem): string {
  return notificationCenterString(item.target?.path) && item.target?.path.startsWith("/") ? item.target.path : "/";
}

function notificationCenterTime(value: string | null): string {
  if (!value || Number.isNaN(Date.parse(value))) return "시간 미상";
  return new Date(value).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
}

// 목록을 날짜 단위로 묶는 머리 — 오늘·어제는 말로, 그 이전은 월·일로.
function notificationCenterDayLabel(value: string | null): string {
  if (!value || Number.isNaN(Date.parse(value))) return "날짜 미상";
  const day = new Date(value);
  const today = new Date();
  const startOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(day)) / 86_400_000);
  if (diffDays === 0) return "오늘";
  if (diffDays === 1) return "어제";
  return day.toLocaleDateString("ko-KR", { month: "long", day: "numeric" });
}

function notificationCenterSetStatus(message: string, tone: "neutral" | "error" = "neutral"): void {
  const { status } = notificationCenterElements();
  if (!status) return;
  status.dataset.tone = tone;
  status.textContent = message;
}

// 사이드바 "알림" 항목과 모바일 헤더 종 아이콘의 안 읽음 카운트 — 알림 unreadCount만 센다
// (안 읽은 메시지 총계·업무 배지와 섞지 않는다).
function notificationCenterSyncTriggerCounts(): void {
  const count = notificationCenterState.unreadCount;
  document.querySelectorAll<HTMLElement>("[data-notification-count]").forEach((el) => {
    el.textContent = count > 99 ? "99+" : `${count}`;
    el.hidden = count <= 0;
  });
  notificationCenterPanelTriggers().forEach((trigger) => {
    trigger.setAttribute("aria-label", count > 0 ? `알림, 안 읽음 ${count}개` : "알림");
  });
}

// "모두 읽음"은 읽을 알림이 없거나 목록 동기화·일괄 읽음 요청 중이면 막는다. unreadCount 가 바뀌는
// 지점(렌더·읽음 처리·배지 갱신)마다 다시 계산한다. 버튼이 없는 마크업이면 아무 것도 하지 않는다.
function notificationCenterSyncReadAllButton(): void {
  const { readAllButton } = notificationCenterElements();
  if (!readAllButton) return;
  readAllButton.disabled = notificationCenterState.loading
    || notificationCenterState.markingAllRead
    || notificationCenterState.unreadCount <= 0;
}

function notificationCenterUpdateUnreadBadge(): void {
  const { unreadBadge } = notificationCenterElements();
  notificationCenterSyncReadAllButton();
  if (!unreadBadge) return;
  unreadBadge.textContent = notificationCenterState.unreadCount > 0 ? `${notificationCenterState.unreadCount}` : "0";
  unreadBadge.dataset.hasUnread = notificationCenterState.unreadCount > 0 ? "true" : "false";
  notificationCenterSyncTriggerCounts();
  // 알림 unreadCount 가 바뀌는 모든 지점(렌더·읽음 처리)이 여기를 지나므로 OS 앱 배지도 여기서
  // 다시 계산한다.
  notificationCenterSyncAppBadge();
}

// 안 읽은 메시지 총계 갱신 지점 — WS message-unread-total 과 앱 시작·재접속 시 복원 REST 응답이
// 공유한다. 앱 배지는 알림 unreadCount 와 합산해 다시 계산한다.
function notificationCenterSetMessageUnreadTotal(total: number): void {
  notificationCenterState.messageUnreadTotal = Number.isFinite(total) && total > 0 ? total : 0;
  notificationCenterState.messageUnreadTotalKnown = true;
  notificationCenterSyncAppBadge();
}

// 앱 아이콘 배지 = 안 읽은 메시지 총계 + 알림 unreadCount(room_message 는 서버에서 이미 제외).
// 알려진 겹침: 멘션·답장 메시지는 방 안 읽음 +1, 알림(room_mention/room_reply) +1 로 배지에서
// 2로 센다 — 사용자가 고른 합 방식(2026-10-05)이라 의도적으로 그대로 둔다. 서비스워커의
// syncNotificationAppBadge 와 같은 합이다.
function notificationCenterAppBadgeTotal(): number | null {
  if (!notificationCenterState.unreadCountKnown || !notificationCenterState.messageUnreadTotalKnown) return null;
  return notificationCenterState.messageUnreadTotal + Math.max(0, notificationCenterState.unreadCount);
}

function notificationCenterSyncAppBadge(): void {
  const total = notificationCenterAppBadgeTotal();
  if (total === null) return;
  const appBadgeNavigator = navigator as Navigator & {
    setAppBadge?: (count?: number) => Promise<void> | void;
    clearAppBadge?: () => Promise<void> | void;
  };
  try {
    const badgeCall = total > 0
      ? appBadgeNavigator.setAppBadge?.(total)
      : appBadgeNavigator.clearAppBadge?.();
    void Promise.resolve(badgeCall).catch(() => {});
  } catch {
    // fail-soft: App Badge API 오류가 알림센터 렌더링을 막지 않게 한다.
  }
}

function notificationCenterRenderList(): void {
  const { list, moreButton, refreshButton } = notificationCenterElements();
  if (!list || !moreButton || !refreshButton) return;

  refreshButton.disabled = notificationCenterState.loading;
  notificationCenterSyncReadAllButton();
  moreButton.disabled = notificationCenterState.loading || notificationCenterState.loadingMore || !notificationCenterState.nextCursor;
  moreButton.hidden = !notificationCenterState.nextCursor && notificationCenterState.notifications.length <= 0 ? true : !notificationCenterState.nextCursor;

  if (notificationCenterState.loading && notificationCenterState.notifications.length === 0) {
    list.innerHTML = '<li class="notification-center__empty" data-state="loading">알림을 불러오는 중이에요…</li>';
    notificationCenterSetStatus("알림 목록을 동기화하는 중이에요.");
    return;
  }

  if (notificationCenterState.error && notificationCenterState.notifications.length === 0) {
    list.innerHTML = '<li class="notification-center__empty" data-state="error">알림을 불러오지 못했어요. 새로고침으로 다시 시도해 주세요.</li>';
    notificationCenterSetStatus(notificationCenterState.error, "error");
    return;
  }

  if (notificationCenterState.notifications.length === 0) {
    list.innerHTML = '<li class="notification-center__empty" data-state="empty">아직 도착한 알림이 없어요.</li>';
    notificationCenterSetStatus("읽을 알림이 없어요.");
    return;
  }

  let currentDay = "";
  list.innerHTML = notificationCenterState.notifications.map((item) => {
    const path = notificationCenterPath(item);
    const isReading = notificationCenterState.activeReadId === item.id;
    const kind = notificationCenterTypeKind(item.type);
    const day = notificationCenterDayLabel(item.createdAt);
    const group = day !== currentDay
      ? `<li class="notification-center__group" role="presentation">${notificationCenterEscape(day)}</li>`
      : "";
    currentDay = day;
    const retry = notificationCenterState.error && notificationCenterState.retryNotificationId === item.id
      ? `<div class="notification-center__retry">
          <span>${notificationCenterEscape(notificationCenterState.error)}</span>
          <button type="button" class="notification-center__retry-btn" data-action="retry-read" data-id="${notificationCenterEscape(item.id)}">다시 시도</button>
        </div>`
      : "";
    const body = notificationCenterBody(item);
    return `${group}<li class="notification-center__item${item.unread ? " is-unread" : ""}" data-id="${notificationCenterEscape(item.id)}" data-path="${notificationCenterEscape(path)}">
      <button type="button" class="notification-center__card" data-action="open-notification" data-id="${notificationCenterEscape(item.id)}" aria-busy="${isReading ? "true" : "false"}">
        <span class="notification-center__dot" aria-hidden="true"></span>
        <span class="notification-center__content">
          <span class="notification-center__meta">
            <span class="notification-center__chip" data-kind="${kind}">${notificationCenterEscape(notificationCenterTypeLabel(item.type))}</span>
            <span class="notification-center__time">${notificationCenterEscape(notificationCenterTime(item.createdAt))}</span>
            ${item.unread ? '<span class="notification-center__sr">읽지 않음</span>' : ""}
          </span>
          <span class="notification-center__item-title">${notificationCenterEscape(notificationCenterTitle(item))}</span>
          ${body ? `<span class="notification-center__body">${notificationCenterEscape(body)}</span>` : ""}
        </span>
      </button>
      ${retry}
    </li>`;
  }).join("");

  notificationCenterSetStatus(
    notificationCenterState.error
      ? notificationCenterState.error
      : (notificationCenterState.unreadCount > 0
          ? `읽지 않은 알림 ${notificationCenterState.unreadCount}건이 있어요.`
          : "모든 알림을 읽었어요."),
    notificationCenterState.error ? "error" : "neutral",
  );

  if (notificationCenterState.focusNotificationId) {
    const focusTarget = Array.from(list.querySelectorAll<HTMLElement>(".notification-center__item"))
      .find((item) => item.dataset.id === notificationCenterState.focusNotificationId)
      ?.querySelector<HTMLElement>(".notification-center__card");
    focusTarget?.focus();
    notificationCenterState.focusNotificationId = null;
  }
}

async function notificationCenterRequest(url: string, options: RequestInit = {}): Promise<Record<string, unknown>> {
  const response = await fetch(url, {
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(options.method ? { "content-type": "application/json" } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });
  const payload = await response.json() as Record<string, unknown>;
  if (!response.ok || payload.ok !== true) {
    throw new Error(notificationCenterString(payload.error) ? payload.error : "notification_center_request_failed");
  }
  return payload;
}

async function fetchNotificationCenterPage(cursor: string | null = null): Promise<{
  notifications: NotificationCenterItem[];
  unreadCount: number;
  nextCursor: string | null;
}> {
  const params = new URLSearchParams({ limit: String(NOTIFICATION_CENTER_PAGE_SIZE) });
  if (cursor) params.set("cursor", cursor);
  const payload = await notificationCenterRequest(`/api/notifications?${params.toString()}`) as NotificationCenterPayload;
  const notifications = Array.isArray(payload.notifications)
    ? payload.notifications.map(normalizeNotificationCenterItem).filter((item): item is NotificationCenterItem => Boolean(item))
    : [];
  return {
    notifications,
    unreadCount: typeof payload.unreadCount === "number" ? payload.unreadCount : 0,
    nextCursor: notificationCenterString(payload.nextCursor) ? payload.nextCursor : null,
  };
}

async function fetchNotificationCenterUnreadCount(): Promise<number> {
  const payload = await notificationCenterRequest("/api/notifications/unread-count") as NotificationCenterPayload;
  return typeof payload.unreadCount === "number" ? payload.unreadCount : 0;
}

// 앱 배지 복원용 — actor 기준 안 읽은 메시지 총계는 알림 unread-count와 별개 REST 경로(rooms
// 라우터)에서 온다. 앱 배지는 이 값과 알림 unreadCount 의 합이다.
async function fetchMessageUnreadTotal(): Promise<number> {
  const payload = await notificationCenterRequest("/api/rooms/unread-count") as NotificationCenterPayload;
  return typeof payload.unreadCount === "number" ? payload.unreadCount : 0;
}

// ws-handlers.ts의 onOpen(최초 접속 + 재접속마다)이 호출한다 — WS push만으로는 접속 공백 동안의
// 변동을 따라잡지 못하므로 연결될 때마다 REST로 현재값을 다시 확정한다. 실패는 배지를 건드리지
// 않고 다음 WS push를 기다린다(fail-soft).
async function restoreMessageUnreadTotal(): Promise<void> {
  try {
    notificationCenterSetMessageUnreadTotal(await fetchMessageUnreadTotal());
  } catch {
    // fail-soft
  }
}

function notificationCenterBroadcastSync(): void {
  localStorage.setItem(NOTIFICATION_CENTER_SYNC_KEY, JSON.stringify({ at: new Date().toISOString() }));
}

async function renderNotificationCenter(options: { reset?: boolean; focusId?: string | null } = {}): Promise<void> {
  const { section } = notificationCenterElements();
  if (!section) return;
  if (options.focusId) notificationCenterState.focusNotificationId = options.focusId;
  if (options.reset) {
    notificationCenterState.loading = true;
    notificationCenterState.error = null;
    notificationCenterRenderList();
  }
  try {
    const page = await fetchNotificationCenterPage(options.reset ? null : notificationCenterState.nextCursor);
    notificationCenterState.notifications = options.reset
      ? page.notifications
      : [...notificationCenterState.notifications, ...page.notifications];
    notificationCenterState.unreadCount = page.unreadCount;
    notificationCenterState.unreadCountKnown = true;
    notificationCenterState.nextCursor = page.nextCursor;
    notificationCenterState.error = null;
  } catch (error) {
    notificationCenterState.error = error instanceof Error ? error.message : "notification_center_sync_failed";
  } finally {
    notificationCenterState.loading = false;
    notificationCenterState.loadingMore = false;
    notificationCenterUpdateUnreadBadge();
    notificationCenterRenderList();
  }
}

function notificationCenterNavigate(path: string): void {
  if (typeof parseViewFromUrl === "function" && typeof router !== "undefined" && router && typeof router.navigate === "function") {
    const target = new URL(path, window.location.origin);
    if (target.pathname === "/work/proposals") {
      const proposalId = target.searchParams.get("proposalId") || undefined;
      router.navigate("work-proposal-review", { params: { proposalId }, proposalId });
      return;
    }
    const parsed = parseViewFromUrl(path);
    if (parsed) {
      router.navigate(parsed.view, { params: parsed.params, proposalId: parsed.params.proposalId });
      return;
    }
  }
  window.location.href = path;
}

async function markNotificationRead(id: string): Promise<NotificationCenterItem> {
  const payload = await notificationCenterRequest(`/api/notifications/${encodeURIComponent(id)}/read`, { method: "POST", body: "{}" });
  const notification = normalizeNotificationCenterItem(payload.notification);
  if (!notification) throw new Error("notification_record_invalid");
  const index = notificationCenterState.notifications.findIndex((item) => item.id === id);
  if (index !== -1) {
    notificationCenterState.notifications[index] = notification;
  }
  try {
    notificationCenterState.unreadCount = await fetchNotificationCenterUnreadCount();
    notificationCenterState.unreadCountKnown = true;
  } catch {
    notificationCenterState.unreadCount = Math.max(0, notificationCenterState.notifications.filter((item) => item.unread).length);
  }
  notificationCenterUpdateUnreadBadge();
  notificationCenterBroadcastSync();
  return notification;
}

// 일괄 읽음의 기준 시각 — 지금 목록에서 가장 최신 알림의 createdAt(서버 시각). 목록을 불러온 뒤
// 새로 도착한 알림은 보지 못한 것이므로 닫지 않는다. 클라이언트 시계를 쓰지 않아 시계 오차에
// 흔들리지 않는다. 목록이 비었거나 시각을 모르면 서버의 now 를 쓴다(before 생략).
function notificationCenterReadAllCutoff(): string | null {
  let newest: string | null = null;
  for (const item of notificationCenterState.notifications) {
    if (!item.createdAt || Number.isNaN(Date.parse(item.createdAt))) continue;
    if (newest === null || Date.parse(item.createdAt) > Date.parse(newest)) newest = item.createdAt;
  }
  return newest;
}

// "모두 읽음" — 성공하면 목록·배지·다른 탭을 다시 맞춘다. 실패는 상태 줄에 오류로만 남긴다(fail-soft).
async function markAllNotificationsRead(): Promise<void> {
  if (notificationCenterState.markingAllRead || notificationCenterState.loading || notificationCenterState.unreadCount <= 0) return;
  notificationCenterState.markingAllRead = true;
  notificationCenterSyncReadAllButton();
  const before = notificationCenterReadAllCutoff();
  try {
    await notificationCenterRequest("/api/notifications/read-all", {
      method: "POST",
      body: JSON.stringify(before ? { before } : {}),
    });
    notificationCenterState.markingAllRead = false;
    await renderNotificationCenter({ reset: true });
    notificationCenterUpdateUnreadBadge();
    notificationCenterBroadcastSync();
  } catch {
    notificationCenterState.markingAllRead = false;
    notificationCenterState.error = "모두 읽음 처리에 실패했어요. 잠시 후 다시 시도해 주세요.";
    notificationCenterRenderList();
    notificationCenterSyncReadAllButton();
  }
}

async function notificationCenterHandleOpen(id: string): Promise<void> {
  const current = notificationCenterState.notifications.find((item) => item.id === id);
  if (!current) return;
  notificationCenterState.activeReadId = id;
  notificationCenterState.focusNotificationId = id;
  notificationCenterState.retryNotificationId = null;
  notificationCenterState.error = null;
  notificationCenterRenderList();
  try {
    const next = current.unread ? await markNotificationRead(id) : current;
    closeNotificationCenterPanel({ restoreFocus: false });
    notificationCenterNavigate(notificationCenterPath(next));
    notificationCenterState.error = null;
    notificationCenterState.retryNotificationId = null;
  } catch (error) {
    notificationCenterState.error = error instanceof Error ? error.message : "notification_read_failed";
    notificationCenterState.retryNotificationId = id;
  } finally {
    notificationCenterState.activeReadId = null;
    notificationCenterRenderList();
  }
}

// ===== 알림 패널 — 사이드바 "알림" / 모바일 헤더 종 아이콘이 연다 =====
// 데스크톱은 사이드바 옆 패널, 모바일(≤767px)은 바텀시트. 보던 화면은 그대로 두고, 항목을 열면
// 대상 화면으로 이동하며 닫힌다. 목록 DOM(id)은 설정 탭 시절과 같아 렌더·읽음 로직을 그대로 쓴다.
let notificationCenterPanelReturnFocus: HTMLElement | null = null;

function notificationCenterPanelTriggers(): HTMLElement[] {
  return ["notificationPanelBtn", "mobileNotificationBtn"]
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null);
}

function isNotificationCenterPanelOpen(): boolean {
  const { section } = notificationCenterElements();
  return Boolean(section?.classList.contains("is-open"));
}

function notificationCenterPanelSetExpanded(expanded: boolean): void {
  notificationCenterPanelTriggers().forEach((trigger) => {
    trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    trigger.classList.toggle("active", expanded && trigger.classList.contains("nav-item"));
  });
}

function openNotificationCenterPanel(trigger: HTMLElement | null = null): void {
  const { section } = notificationCenterElements();
  const scrim = document.getElementById("notificationPanelScrim");
  if (!section || isNotificationCenterPanelOpen()) return;
  notificationCenterPanelReturnFocus = trigger;
  // 모바일 드로어 안의 사이드바 버튼으로 열었으면 드로어를 먼저 닫는다(app.ts 전역 함수).
  (globalThis as typeof globalThis & { closeDrawer?: () => void }).closeDrawer?.();
  section.hidden = false;
  if (scrim) scrim.hidden = false;
  section.classList.add("is-open");
  scrim?.classList.add("is-open");
  document.body.classList.add("notification-panel-open");
  notificationCenterPanelSetExpanded(true);
  document.getElementById("notificationPanelCloseBtn")?.focus();
  void renderNotificationCenter({ reset: true });
}

function closeNotificationCenterPanel(options: { restoreFocus?: boolean } = {}): void {
  const { section } = notificationCenterElements();
  const scrim = document.getElementById("notificationPanelScrim");
  if (!section || !isNotificationCenterPanelOpen()) return;
  section.classList.remove("is-open");
  scrim?.classList.remove("is-open");
  section.hidden = true;
  if (scrim) scrim.hidden = true;
  document.body.classList.remove("notification-panel-open");
  notificationCenterPanelSetExpanded(false);
  if (options.restoreFocus !== false) notificationCenterPanelReturnFocus?.focus();
  notificationCenterPanelReturnFocus = null;
}

function bindNotificationCenterPanel(): void {
  notificationCenterPanelTriggers().forEach((trigger) => {
    if (trigger.dataset.bound === "true") return;
    trigger.dataset.bound = "true";
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      if (isNotificationCenterPanelOpen()) closeNotificationCenterPanel();
      else openNotificationCenterPanel(trigger);
    });
  });
  document.getElementById("notificationPanelCloseBtn")?.addEventListener("click", () => closeNotificationCenterPanel());
  document.getElementById("notificationPanelScrim")?.addEventListener("click", () => closeNotificationCenterPanel());
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isNotificationCenterPanelOpen()) closeNotificationCenterPanel();
  });
  // 다른 화면으로 이동하면(상단 네비·더보기 시트 등) 패널을 닫는다.
  document.addEventListener("view-navigated", () => closeNotificationCenterPanel({ restoreFocus: false }));
}

function bindNotificationCenter(): void {
  const { section, refreshButton, readAllButton, moreButton, list } = notificationCenterElements();
  if (!section || section.dataset.bound === "true" || !refreshButton || !moreButton || !list) return;
  section.dataset.bound = "true";
  refreshButton.addEventListener("click", () => {
    void renderNotificationCenter({ reset: true });
  });
  readAllButton?.addEventListener("click", () => {
    void markAllNotificationsRead();
  });
  moreButton.addEventListener("click", () => {
    if (!notificationCenterState.nextCursor || notificationCenterState.loadingMore) return;
    notificationCenterState.loadingMore = true;
    notificationCenterRenderList();
    void renderNotificationCenter({ reset: false });
  });
  list.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const openButton = target?.closest<HTMLElement>("[data-action='open-notification']");
    if (openButton?.dataset.id) {
      void notificationCenterHandleOpen(openButton.dataset.id);
      return;
    }
    const retryButton = target?.closest<HTMLElement>("[data-action='retry-read']");
    if (retryButton?.dataset.id) {
      void notificationCenterHandleOpen(retryButton.dataset.id);
    }
  });
  bindNotificationCenterPanel();
  window.addEventListener("storage", (event) => {
    if (event.key === NOTIFICATION_CENTER_SYNC_KEY) {
      void renderNotificationCenter({ reset: true });
    }
  });
  void renderNotificationCenter({ reset: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindNotificationCenter, { once: true });
} else {
  bindNotificationCenter();
}

(globalThis as typeof globalThis & {
  BigtionAXNotificationCenter?: {
    renderNotificationCenter(options?: { reset?: boolean; focusId?: string | null }): Promise<void>;
    markNotificationRead(id: string): Promise<NotificationCenterItem>;
    markAllNotificationsRead(): Promise<void>;
    openNotification(id: string): Promise<void>;
    openPanel(): void;
    closePanel(): void;
    // ws-handlers.ts가 WS push/onOpen 복원 시 호출하는 앱 배지 메시지 쪽 소스 갱신 훅.
    onMessageUnreadTotal(unreadCount: number): void;
    restoreMessageUnreadTotal(): Promise<void>;
  };
}).BigtionAXNotificationCenter = {
  renderNotificationCenter,
  markNotificationRead,
  markAllNotificationsRead,
  openNotification: notificationCenterHandleOpen,
  openPanel: () => openNotificationCenterPanel(),
  closePanel: () => closeNotificationCenterPanel(),
  onMessageUnreadTotal: notificationCenterSetMessageUnreadTotal,
  restoreMessageUnreadTotal,
};
